import { FeatureNameEnum } from '@ulysses-inc/harami_api_client'
import React, { useEffect } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import Analytics from 'src/features/analytics/Analytics'
import ApprovalFlows from 'src/features/approvalFlows/ApprovalFlows'
import ApprovalListScene from 'src/features/approvals/ApprovalListScene'
import { CampaignBannerWrapper } from 'src/features/campaignBanner'
import CompanySettingsContainer from 'src/features/companySettings/CompanySettings'
import DashboardContainer from 'src/features/dashboard/Dashboard'
import PlaceDashboard from 'src/features/dashboardPlace/PlaceDashboard'
import Devices from 'src/features/devices/Devices'
import Employees from 'src/features/employees/Employees'
import Error404 from 'src/features/error/Error404'
import EditExcelConversionFlowContainer from 'src/features/excelConversionFlows/EditExcelConversionFlow'
import ExcelConversionFlows from 'src/features/excelConversionFlows/ExcelConversionFlows'
import ExcelConversionLogs from 'src/features/excelConversionLogs/ExcelConversionLogs'
import { useLimitedFeatureAvailability } from 'src/features/featureAvailability/useLimitedFeatureAvailability'
import { useFeature } from 'src/features/featureFlags/useFeatureFlags'
import { setUserPropertiesFromLocalStorage } from 'src/features/firebase/userProperties'
import { GlobalNavigation } from 'src/features/globalNavigation/GlobalNavigation'
import { GlossaryListPage } from 'src/features/glossary/GlossaryListPage'
import { EditGridVariables } from 'src/features/gridVariables/edit/EditGridVariables'
import GridVariableTemplates from 'src/features/gridVariables/list/Templates'
import ImproveExcelConversionLogsContainer from 'src/features/improveExcelConversionLogs/ImproveExcelConversionLogsContainer'
import ImproveDetailContainer from 'src/features/improves/improveDetail/ImproveDetailContainer'
import ImprovesContainer from 'src/features/improves/improveList/ImprovesContainer'
import IndicatedIssueLabels from 'src/features/improves/labels/IndicatedIssueLabels'
import { BarcodeSettingPage } from 'src/features/labelMaster/barcodeSetting/BarcodeSettingPage'
import { LabelMasterEditPage } from 'src/features/labelMaster/edit/LabelMasterEditPage'
import { LabelMasterListPage } from 'src/features/labelMaster/list/LabelMasterListPage'
import MasterDetail from 'src/features/masters/MasterDetail/MasterDetail'
import Masters from 'src/features/masters/Masters'
import { MonitoringDashboard } from 'src/features/monitoringDashboardAlpha/MonitoringDashboard'
import { MonitoringDashboardDetail } from 'src/features/monitoringDashboardAlpha/detail/MonitoringDashboardDetail'
import { OptionUsages } from 'src/features/optionUsages/OptionUsages'
import PlaceGroups from 'src/features/places/PlaceGroups'
import Places from 'src/features/places/Places'
import { ReportExportDetailV2 } from 'src/features/reportExport/detail/ReportExportDetailV2'
import { ReportExportList } from 'src/features/reportExport/list/ReportExportList'
import { TempReportListPageSwitcher } from 'src/features/reports/listBeta/featurePreview/TempReportListPageSwitcher'
import ReportResult from 'src/features/reports/result/ReportResult'
import { ScheduledReportSummary } from 'src/features/scheduledReportSummary/ScheduledReportSummary'
import Schedules from 'src/features/schedules/Schedules'
import EditSchedule from 'src/features/schedules/editSchedule/EditSchedule'
import { ShipmentReadinessOverview } from 'src/features/shipmentReadinessOverview/ShipmentReadinessOverview'
import { CreateTaskSchedulePage } from 'src/features/taskSchedules/CreateTaskSchedulePage'
import MultipleChoiceSets from 'src/features/template/multipleChoiceSets/MultipleChoiceSets'
import EditMultipleChoiceSetContainer from 'src/features/template/multipleChoiceSets/editMultipleChoiceSet/EditMultipleChoiceSet'
import Templates from 'src/features/template/templateList/Templates'
import { TraceKeysListPage } from 'src/features/template/traceKeyList/TraceKeysListPage'
import { CreateTraceKeyPage } from 'src/features/template/traceKeyList/createTraceKey/CreateTraceKeyPage'
import { EditTraceKeyPage } from 'src/features/template/traceKeyList/editTraceKey/EditTraceKeyPage'
import EditGridTemplate from 'src/features/templateEdit/EditGridTemplate'
import { EditTemplate } from 'src/features/templateEdit/EditTemplate'
import CreateTemplateIndustry from 'src/features/templateEdit/onboarding/CreateTemplateIndustry'
import CreateTemplateIndustryDynamic from 'src/features/templateEdit/onboardingDynamic/CreateTemplateIndustryDynamic'
import UserGroups from 'src/features/users/UserGroups'
import Users from 'src/features/users/Users'
import { ChannelTalk } from 'src/features/users/channelTalk/ChannelTalk'
import {
  editorPermissions,
  hasPermission,
} from 'src/libraries/roles/permissions'
import { PrivateRoute } from 'src/views/components/PrivateRoute'

/**
 * 認証が必要な画面へのルーティングを設定しているコンポーネント
 *
 * ルートの設定には、まずユーザーがその画面にアクセスできるかどうかを確認する必要がある。
 * この確認は認証が完了した後にしか行えない。
 * そのため、パブリックなルートとプライベートなルートのコンポーネントを分割したうえで、
 * 認証後に権限の確認を行うように構成している。
 */
export const RoutesPrivate: React.FC = () => {
  // このフックのなかで、ユーザーロール、契約プラン、フィーチャーフラグの情報を取得し、利用可否を判定している。
  const { isFeatureAvailable } = useLimitedFeatureAvailability()
  const { canUse: canUseBarcodeExtractionRule } = useFeature(
    FeatureNameEnum.BARCODE_EXTRACTION_RULE,
  )

  useEffect(() => {
    setUserPropertiesFromLocalStorage()
  }, [])

  return (
    <>
      <Switch>
        <Route
          exact
          // サイドメニューを持つ画面群
          // ここと配下Routeの2カ所にpathの記載が必要なので注意
          // TODO: サイドメニューを持つ画面群は近日中に全てここに集約する予定
          path={[
            '/approvalFlows',
            '/approvals',
            '/companySettings',
            '/dashboards',
            '/excelConversionFlows',
            '/improves',
            '/indicatedIssueLabels',
            '/masters',
            '/monitoringDashboardAlpha',
            '/multipleChoiceSets',
            '/placeGroups',
            '/places',
            '/reportExports',
            '/scheduledReportSummary',
            '/schedules',
            '/userGroups',
            '/users',
            '/devices',
            '/optionUsages',
            '/analytics',
            '/shipmentReadinessOverview',
            '/labelMasters',
            '/glossaries',
          ]}
        >
          <GlobalNavigation>
            <CampaignBannerWrapper>
              <Switch>
                <PrivateRoute
                  exact
                  path="/approvalFlows"
                  component={ApprovalFlows}
                  hasPermission={isFeatureAvailable('approvalFlows')}
                />
                <PrivateRoute
                  exact
                  path="/approvals"
                  component={ApprovalListScene}
                  hasPermission={isFeatureAvailable('approvals')}
                />
                <PrivateRoute
                  exact
                  path="/companySettings"
                  component={CompanySettingsContainer}
                  hasPermission={isFeatureAvailable('companySettings')}
                />
                <PrivateRoute
                  exact
                  path="/dashboards"
                  component={DashboardContainer}
                  hasPermission={isFeatureAvailable('dashboard')}
                />
                <PrivateRoute
                  exact
                  path="/excelConversionFlows"
                  component={ExcelConversionFlows}
                  hasPermission={isFeatureAvailable('excelConversionRead')}
                />
                <PrivateRoute
                  exact
                  path="/improves"
                  component={ImprovesContainer}
                  hasPermission={isFeatureAvailable('improves')}
                />
                <PrivateRoute
                  exact
                  path="/indicatedIssueLabels"
                  component={IndicatedIssueLabels}
                  hasPermission={isFeatureAvailable('improves')}
                />
                <PrivateRoute
                  exact
                  path="/masters"
                  component={Masters}
                  hasPermission={isFeatureAvailable('masters')}
                />
                <PrivateRoute
                  exact
                  path="/monitoringDashboardAlpha"
                  component={MonitoringDashboard}
                  hasPermission={isFeatureAvailable('monitoringDashboardAlpha')}
                />
                <PrivateRoute
                  exact
                  path="/multipleChoiceSets"
                  component={MultipleChoiceSets}
                  hasPermission={isFeatureAvailable('multipleChoiceSets')}
                />
                <PrivateRoute
                  exact
                  path="/placeGroups"
                  component={PlaceGroups}
                  hasPermission={isFeatureAvailable('places')}
                />
                <PrivateRoute
                  exact
                  path="/places"
                  component={Places}
                  hasPermission={isFeatureAvailable('places')}
                />
                <PrivateRoute
                  exact
                  path="/reportExports"
                  component={ReportExportList}
                  hasPermission={isFeatureAvailable('reportExports')}
                />
                <PrivateRoute
                  exact
                  path="/scheduledReportSummary"
                  component={ScheduledReportSummary}
                  hasPermission={isFeatureAvailable('scheduledReportSummary')}
                />
                <PrivateRoute
                  exact
                  path="/schedules"
                  component={Schedules}
                  hasPermission={isFeatureAvailable('schedules')}
                />
                <PrivateRoute
                  exact
                  path="/userGroups"
                  component={UserGroups}
                  hasPermission={isFeatureAvailable('users')}
                />
                <PrivateRoute
                  exact
                  path="/users"
                  component={Users}
                  hasPermission={isFeatureAvailable('users')}
                />
                <PrivateRoute
                  exact
                  path="/devices"
                  component={Devices}
                  hasPermission={isFeatureAvailable('devices')}
                />
                <PrivateRoute
                  exact
                  path="/optionUsages"
                  component={OptionUsages}
                  hasPermission={isFeatureAvailable('labelCheck')}
                />
                <PrivateRoute
                  exact
                  path="/analytics"
                  component={Analytics}
                  hasPermission={isFeatureAvailable('analytics')}
                />
                <PrivateRoute
                  exact
                  path="/shipmentReadinessOverview"
                  component={ShipmentReadinessOverview}
                  hasPermission={isFeatureAvailable(
                    'shipmentReadinessOverview',
                  )}
                />
                <PrivateRoute
                  exact
                  path="/labelMasters"
                  component={LabelMasterListPage}
                  hasPermission={isFeatureAvailable('labelCheck')}
                />
                <PrivateRoute
                  exact
                  path="/glossaries"
                  component={GlossaryListPage}
                  hasPermission={isFeatureAvailable('multiLanguageSupport')}
                />
              </Switch>
            </CampaignBannerWrapper>
          </GlobalNavigation>
        </Route>
        <Route exact path="/">
          <Redirect to="/dashboards" />
        </Route>
        <PrivateRoute
          exact
          path="/masters/:masterUuid"
          component={MasterDetail}
          hasPermission={isFeatureAvailable('masters')}
        />
        <PrivateRoute
          exact
          path="/improves/:improveUUID"
          component={ImproveDetailContainer}
          hasPermission={isFeatureAvailable('improves')}
        />
        <PrivateRoute
          exact
          path="/improves/:improveUUID/excelConversionLogs"
          component={ImproveExcelConversionLogsContainer}
          hasPermission={isFeatureAvailable('improves')}
        />
        <PrivateRoute
          exact
          path="/dashboards/places/:placeNodeUUID"
          component={PlaceDashboard}
          hasPermission={isFeatureAvailable('dashboard')}
        />
        <PrivateRoute
          exact
          path="/monitoringDashboardAlpha/detail"
          component={MonitoringDashboardDetail}
          hasPermission={isFeatureAvailable('monitoringDashboardAlpha')}
        />
        <PrivateRoute
          exact
          path="/reports"
          component={TempReportListPageSwitcher}
          hasPermission={isFeatureAvailable('reports')}
        />
        <PrivateRoute
          exact
          path="/reports/:id"
          component={ReportResult}
          hasPermission={isFeatureAvailable('reports')}
        />
        <PrivateRoute
          exact
          path="/templates"
          component={Templates}
          hasPermission={isFeatureAvailable('templates')}
        />
        <PrivateRoute
          exact
          path="/traceKeys"
          component={TraceKeysListPage}
          hasPermission={isFeatureAvailable('reportTrace')}
        />
        <PrivateRoute
          exact
          path="/traceKeys/add"
          component={CreateTraceKeyPage}
          hasPermission={
            isFeatureAvailable('reportTrace') &&
            hasPermission(editorPermissions)
          }
        />
        <PrivateRoute
          exact
          path="/traceKeys/:traceKeyId/edit"
          component={EditTraceKeyPage}
          hasPermission={
            isFeatureAvailable('reportTrace') &&
            hasPermission(editorPermissions)
          }
        />
        <PrivateRoute
          exact
          path="/templates/pages/add"
          component={EditTemplate}
          hasPermission={isFeatureAvailable('templates')}
        />
        <PrivateRoute
          exact
          path="/templates/pages/addGridTemplate"
          component={EditGridTemplate}
          hasPermission={isFeatureAvailable('templates')}
        />
        {/* FIXME: path="/templates/:templateId は利用されている箇所がなさそう(templateEditMode = new になるため既存のひな形情報が取得されない) */}
        <PrivateRoute
          exact
          path="/templates/:templateId"
          component={EditTemplate}
          hasPermission={isFeatureAvailable('templates')}
        />
        <PrivateRoute
          exact
          path="/templates/:id/pages/edit"
          component={EditTemplate}
          hasPermission={isFeatureAvailable('templates')}
        />
        <PrivateRoute
          exact
          path="/templates/:id/pages/editGridTemplate"
          component={EditGridTemplate}
          hasPermission={isFeatureAvailable('templates')}
        />
        <PrivateRoute
          exact
          path="/gridVariables"
          component={GridVariableTemplates}
          hasPermission={isFeatureAvailable('dynamicSheet')}
        />
        <PrivateRoute
          exact
          path="/schedules/add"
          component={EditSchedule}
          hasPermission={isFeatureAvailable('schedules')}
        />
        <PrivateRoute
          exact
          path="/schedules/:scheduleId"
          component={EditSchedule}
          hasPermission={isFeatureAvailable('schedules')}
        />
        <PrivateRoute
          exact
          path="/taskSchedules/add"
          component={CreateTaskSchedulePage}
          hasPermission={isFeatureAvailable('taskSchedules')}
        />
        {
          // 同じパスでコンポーネントを切り替えているためこのような手当てが必要になったもの。
          // コンポーネント内での切り替えの方が望ましい。
          isFeatureAvailable('dynamicSheet') ? (
            <PrivateRoute
              exact
              path="/templates/create/industry"
              component={CreateTemplateIndustryDynamic}
              hasPermission
            />
          ) : (
            <PrivateRoute
              exact
              path="/templates/create/industry"
              component={CreateTemplateIndustry}
              hasPermission={isFeatureAvailable('templates')}
            />
          )
        }
        <PrivateRoute
          exact
          path="/templates/create/type"
          component={CreateTemplateIndustry}
          hasPermission={isFeatureAvailable('templates')}
        />
        <PrivateRoute
          exact
          path="/excelConversionFlows/add"
          component={EditExcelConversionFlowContainer}
          hasPermission={isFeatureAvailable('excelConversionEdit')}
        />
        <PrivateRoute
          exact
          path="/excelConversionFlows/:flowId"
          component={EditExcelConversionFlowContainer}
          hasPermission={isFeatureAvailable('excelConversionEdit')}
        />
        <PrivateRoute
          exact
          path="/multipleChoiceSets/add"
          component={EditMultipleChoiceSetContainer}
          hasPermission={isFeatureAvailable('multipleChoiceSets')}
        />
        <PrivateRoute
          exact
          path="/multipleChoiceSets/:multipleChoiceSetId"
          component={EditMultipleChoiceSetContainer}
          hasPermission={isFeatureAvailable('multipleChoiceSets')}
        />
        <PrivateRoute
          exact
          path="/excelConversionLogs"
          component={ExcelConversionLogs}
          hasPermission={isFeatureAvailable('excelConversionRead')}
        />
        <PrivateRoute
          exact
          path="/labelMasters/barcodeSetting"
          component={BarcodeSettingPage}
          hasPermission={
            isFeatureAvailable('labelCheck') &&
            canUseBarcodeExtractionRule === 'yes'
          }
        />
        <PrivateRoute
          exact
          path="/labelMasters/:labelMasterId/edit"
          component={LabelMasterEditPage}
          hasPermission={isFeatureAvailable('labelCheck')}
        />
        <PrivateRoute
          exact
          path="/employees"
          component={Employees}
          hasPermission={isFeatureAvailable('employees')}
        />
        <PrivateRoute
          exact
          path="/gridVariables/:templateId/edit"
          component={EditGridVariables}
          hasPermission={isFeatureAvailable('dynamicSheet')}
        />
        <PrivateRoute
          exact
          path="/reportExports/:reportExportId"
          component={ReportExportDetailV2}
          hasPermission={isFeatureAvailable('reportExports')}
        />
        <Route>
          <Error404 />
        </Route>
      </Switch>
      <ChannelTalk />
    </>
  )
}
